import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiFileText, FiPlus, FiUsers, FiMail, FiTrendingUp, FiCheck, FiSearch, FiChevronDown, FiMoreHorizontal, FiUser, FiAlertCircle, FiCheckCircle, FiTrash2 } from 'react-icons/fi';
import { IGlobalState } from '../../../../model/IGlobalState';
import { toast } from '../../Toast';
import { ContactRow, useContactsData, listName, initials, contactKey, isNewInDays } from './contactsData';
import AddContactModal from './AddContactModal';
import NewListModal from './NewListModal';
import ExportContactsModal from './ExportContactsModal';

type Status = 'All' | 'Subscribed' | 'Unsubscribed';
// sidebar picks: a list id, or one of the built-in segments
type View = { kind: 'all' } | { kind: 'list'; id: string } | { kind: 'new' } | { kind: 'unsubscribed' };

const PAGE_SIZES = [10, 25, 50, 100];
const preview = (what: string) => toast.info(`Preview only: ${what} isn’t connected yet`);

// Communications › Contacts (specs 219:10187 list + row menu, 587:4547 bulk selection). Reads the client's real contacts;
// every change (add, tag, unsubscribe, delete, new list) is preview only — nothing is written.
const Contacts: React.FC = () => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const short = (stock?.companyName || stock?.ticker || '').replace(/\s+(limited|ltd\.?)$/i, '');
    const { data, error } = useContactsData(stock?.id);
    const [view, setView] = useState<View>({ kind: 'all' });
    const [q, setQ] = useState('');
    const [status, setStatus] = useState<Status>('All');
    const [tag, setTag] = useState('Any');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [menu, setMenu] = useState<string | null>(null);
    const [modal, setModal] = useState<'add' | 'list' | 'export' | null>(null);

    const contacts = data?.contacts ?? [];
    const tags = useMemo(() => Array.from(new Set(contacts.flatMap(c => c.tags))).sort(), [contacts]);
    const inView = (c: ContactRow) =>
        view.kind === 'all' ? true
            : view.kind === 'list' ? c.listIds.includes(view.id)
                : view.kind === 'new' ? isNewInDays(c, 30)
                    : !c.subscribed;
    const term = q.trim().toLowerCase();
    const shown = contacts.filter(c => inView(c)
        && (status === 'All' || (status === 'Subscribed') === c.subscribed)
        && (tag === 'Any' || c.tags.includes(tag))
        && (!term || c.name.toLowerCase().includes(term) || c.email.toLowerCase().includes(term)));
    const pages = Math.max(1, Math.ceil(shown.length / pageSize));
    const first = (page - 1) * pageSize;
    const rows = shown.slice(first, first + pageSize);

    // filters change what's shown: back to page 1, and drop a selection the user can no longer see
    useEffect(() => { setPage(1); setSelected(new Set()); }, [view, q, status, tag, pageSize]);

    const subscribed = contacts.filter(c => c.subscribed).length;
    const newIn30 = contacts.filter(c => isNewInDays(c, 30)).length;
    const newIn7 = contacts.filter(c => isNewInDays(c, 7)).length;

    const toggle = (email: string) => setSelected(s => {
        const next = new Set(s);
        if (next.has(email)) next.delete(email);
        else next.add(email);
        return next;
    });
    const allOnPage = rows.length > 0 && rows.every(r => selected.has(r.email));
    const toggleAll = () => setSelected(s => {
        const next = new Set(s);
        rows.forEach(r => { if (allOnPage) next.delete(r.email); else next.add(r.email); });
        return next;
    });

    const open = (c: ContactRow) => history.push(`/communications/contacts/${contactKey(c.email)}`);

    const viewLabel = view.kind === 'all' ? 'All contacts'
        : view.kind === 'list' ? listName(data?.lists.find(l => l.id === view.id) ?? { id: '', numSubscribers: 0 })
            : view.kind === 'new' ? 'Signed up this month' : 'Unsubscribed';

    const sideItem = (v: View, label: string, count: number, key: string) => {
        const active = JSON.stringify(v) === JSON.stringify(view);
        return <button key={key} type="button" className={active ? 'active' : ''} onClick={() => setView(v)}>
            <span>{label}</span><span className="n">{count.toLocaleString()}</span>
        </button>;
    };

    return <div className="announcements contacts-v2">
        <div className="page-header">
            <div>
                <h1>Contacts</h1>
                <p>Everyone who has agreed to hear from {short}, and which lists they are on</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" onClick={() => setModal('export')} disabled={!data}><FiFileText /> Export</button>
                <button type="button" className="btn-outline" onClick={() => history.push('/communications/contacts/import')}><FiPlus /> Import CSV</button>
                <button type="button" className="btn-primary" onClick={() => setModal('add')}><FiPlus /> Add Contact</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="stats">
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiUsers /></span> Subscribed</div>
                <div className="v">{data ? subscribed.toLocaleString() : '—'}</div>
                <div className="d">{data && contacts.length ? `${(subscribed * 100 / contacts.length).toFixed(1)}% of the list` : ' '}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiMail /></span> Unsubscribed</div>
                <div className="v">{data ? (contacts.length - subscribed).toLocaleString() : '—'}</div>
                <div className="d">{data ? `of ${contacts.length.toLocaleString()} contacts` : ' '}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiTrendingUp /></span> New in 30 days {data && newIn7 > 0 && <span className="pill ok">+{newIn7}</span>}</div>
                <div className="v">{data ? newIn30.toLocaleString() : '—'}</div>
                <div className="d">{data ? `${newIn7} in the last 7 days` : ' '}</div>
            </div>
            <div className="stat">
                <div className="h"><span className="icon-tile"><FiCheck /></span> Matched to registry</div>
                <div className="v">—</div>
                <div className="d">Registry matching isn’t connected yet</div>
            </div>
        </div>

        <div className="contacts-row">
            <nav className="card contacts-side" aria-label="Lists and segments">
                <div className="nav-title">Lists &amp; segments</div>
                {sideItem({ kind: 'all' }, 'All contacts', contacts.length, 'all')}
                {(data?.lists ?? []).map(l => sideItem({ kind: 'list', id: l.id }, listName(l), contacts.filter(c => c.listIds.includes(l.id)).length, l.id))}
                <hr />
                {sideItem({ kind: 'new' }, 'Signed up this month', newIn30, 'new')}
                {sideItem({ kind: 'unsubscribed' }, 'Unsubscribed', contacts.length - subscribed, 'unsub')}
                <button type="button" className="new-list" onClick={() => setModal('list')}><FiPlus /> New list or segment</button>
            </nav>

            <div className="card contacts-main">
                <div className="filters">
                    <label className="search"><FiSearch /><input placeholder="Search by name or email…" value={q} onChange={e => setQ(e.target.value)} /></label>
                    <label className="filter">
                        <select aria-label="Status" value={status} onChange={e => setStatus(e.target.value as Status)}>{(['All', 'Subscribed', 'Unsubscribed'] as Status[]).map(o => <option key={o} value={o}>Status: {o}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <label className="filter">
                        <select aria-label="Tags" value={tag} onChange={e => setTag(e.target.value)}>{['Any', ...tags].map(o => <option key={o} value={o}>Tags: {o}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <span className="count">{shown.length.toLocaleString()} contacts</span>
                </div>

                {selected.size > 0 && <div className="contacts-bulk">
                    <FiCheckCircle />
                    <strong>{selected.size} contact{selected.size === 1 ? '' : 's'} selected</strong>
                    {selected.size < shown.length && <button type="button" className="all" onClick={() => setSelected(new Set(shown.map(c => c.email)))}>Select all {shown.length}</button>}
                    <span className="sp" />
                    <button type="button" onClick={() => preview('adding to a list')}><FiUsers /> Add to List</button>
                    <button type="button" onClick={() => preview('tagging')}><FiPlus /> Add Tag</button>
                    <button type="button" onClick={() => preview('unsubscribing')}><FiMail /> Unsubscribe</button>
                    <button type="button" className="danger" onClick={() => preview('deleting contacts')}><FiTrash2 /> Delete</button>
                    <button type="button" onClick={() => setSelected(new Set())}>Clear</button>
                </div>}

                <div className="table">
                    <div className="row head">
                        <span className="cb"><input type="checkbox" aria-label="Select all on this page" checked={allOnPage} onChange={toggleAll} /></span>
                        <span>Contact</span><span>Status</span><span>Tags</span><span>Source</span><span>Added</span><span />
                    </div>
                    {!data && !error && <p className="loading">Loading contacts…</p>}
                    {rows.map(c => <div key={c.email} className={`row${selected.has(c.email) ? ' picked' : ''}`} onClick={() => open(c)}>
                        <span className="cb" onClick={e => e.stopPropagation()}><input type="checkbox" aria-label={`Select ${c.name || c.email}`} checked={selected.has(c.email)} onChange={() => toggle(c.email)} /></span>
                        <span className="who">
                            <span className="av">{initials(c.name, c.email)}</span>
                            <span className="t"><strong>{c.name || c.email}</strong><span>{c.email}</span></span>
                        </span>
                        <span><span className={`pill ${c.subscribed ? 'ok' : 'bad'}`}>{c.subscribed ? 'Subscribed' : 'Unsubscribed'}</span></span>
                        <span className="tags">{c.tags.length ? c.tags.slice(0, 2).map(t => <span key={t} className={`tag${t.toLowerCase() === 'shareholder' ? ' cyan' : ''}`}>{t}</span>) : <span className="none">—</span>}</span>
                        <span className="src">{c.consentSource || '—'}</span>
                        <span className="date">{moment(c.created).format('DD MMM YY')}</span>
                        <span className="more" onClick={e => e.stopPropagation()}>
                            <RowMenu open={menu === c.email} onToggle={() => setMenu(menu === c.email ? null : c.email)} onClose={() => setMenu(null)}
                                onView={() => open(c)} subscribed={c.subscribed} />
                        </span>
                    </div>)}
                    {data && shown.length === 0 && <p className="empty">{contacts.length ? 'No contacts match' : 'No contacts yet — add one or import a file'}</p>}
                </div>

                <div className="footer">
                    <span className="count">Showing {rows.length ? first + 1 : 0}–{first + rows.length} of {shown.length.toLocaleString()} contacts{selected.size > 0 && ` · ${selected.size} selected`} · {viewLabel}</span>
                    <label className="rows">Rows per page:
                        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>{PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <Pagination page={page} pages={pages} onPage={setPage} />
                </div>
            </div>
        </div>

        {modal === 'add' && data && <AddContactModal lists={data.lists} onClose={() => setModal(null)} />}
        {modal === 'list' && <NewListModal contacts={contacts} onClose={() => setModal(null)} />}
        {modal === 'export' && data && <ExportContactsModal view={viewLabel} shown={shown} selected={contacts.filter(c => selected.has(c.email))} lists={data.lists} onClose={() => setModal(null)} />}
    </div>;
};

// ⋯ on a row: View & Edit, Add to List, Add Tag, Unsubscribe, Delete (spec 219:10187)
const RowMenu: React.FC<{ open: boolean; subscribed: boolean; onToggle: () => void; onClose: () => void; onView: () => void }> = ({ open, subscribed, onToggle, onClose, onView }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, [open, onClose]);
    const item = (icon: React.ReactNode, label: string, act: () => void, danger = false) =>
        <button type="button" className={danger ? 'danger' : ''} onClick={() => { onClose(); act(); }}>{icon} {label}</button>;
    return <div className="contacts-menu" ref={ref}>
        <button type="button" className="dots" aria-label="Contact actions" aria-expanded={open} onClick={onToggle}><FiMoreHorizontal /></button>
        {open && <div className="pop">
            {item(<FiUser />, 'View & Edit Contact', onView)}
            {item(<FiUsers />, 'Add to List…', () => preview('adding to a list'))}
            {item(<FiFileText />, 'Add Tag…', () => preview('tagging'))}
            <hr />
            {subscribed && item(<FiAlertCircle />, 'Unsubscribe', () => preview('unsubscribing'), true)}
            {item(<FiAlertCircle />, 'Delete Contact', () => preview('deleting a contact'), true)}
        </div>}
    </div>;
};

// ‹ 1 2 3 … 7 › — first, last and the pages around the current one
const Pagination: React.FC<{ page: number; pages: number; onPage: (p: number) => void }> = ({ page, pages, onPage }) => {
    const nums = Array.from({ length: pages }, (_, i) => i + 1).filter(p => p === 1 || p === pages || Math.abs(p - page) <= 1);
    return <div className="pagination">
        <button type="button" disabled={page === 1} onClick={() => onPage(page - 1)}>‹</button>
        {nums.map((p, i) => <React.Fragment key={p}>
            {i > 0 && p - nums[i - 1] > 1 && <span className="gap">…</span>}
            <button type="button" className={p === page ? 'active' : ''} onClick={() => onPage(p)}>{p}</button>
        </React.Fragment>)}
        <button type="button" disabled={page === pages} onClick={() => onPage(page + 1)}>›</button>
    </div>;
};

export default Contacts;
